import { Star, X } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '@/lib/cn';
import { useT } from '@/lib/i18n/useT';
import { useBrandScopedFavorites, useRemoveFavorite } from './api';

/**
 * Favorites list rendered in the left Sidebar, below the Settings item.
 * In `collapsed` mode the list shows just the icons with a tooltip on hover.
 */
export function SidebarFavorites({ collapsed }: { collapsed: boolean }) {
  const t = useT();
  const location = useLocation();
  const { data: favorites = [], isLoading } = useBrandScopedFavorites();
  const removeFavorite = useRemoveFavorite();

  if (isLoading || favorites.length === 0) return null;

  return (
    <div className="mt-1">
      {/* Separator + section header */}
      <div className="my-2 flex items-center gap-2 px-1">
        <span className="h-px flex-1 bg-[var(--color-chrome-border)]" />
        {!collapsed && (
          <span
            className="text-[9px] font-extrabold uppercase tracking-[0.18em] text-[var(--color-chrome-fg-muted)]"
            style={{ fontFamily: 'var(--font-display)' }}
          >
            {t('sidebar.favorites')}
          </span>
        )}
        <span className="h-px flex-1 bg-[var(--color-chrome-border)]" />
      </div>

      <ul className="flex flex-col gap-0.5">
        {favorites.map((f) => {
          const active = location.pathname + (location.search || '') === f.path;
          return (
            <li key={f.id} className="group/fav relative">
              <Link
                to={f.path}
                title={collapsed ? f.name : undefined}
                className={cn(
                  'flex items-center rounded-lg border border-transparent transition-colors',
                  collapsed ? 'justify-center py-2' : 'gap-2.5 py-2 pl-3 pr-8',
                  active
                    ? 'border-[var(--color-chrome-accent)] bg-[var(--color-chrome-accent-soft)] text-[var(--color-chrome-accent-ink)]'
                    : 'text-[var(--color-chrome-fg)] hover:bg-[var(--color-chrome-bg-elevated)]',
                )}
              >
                <Star
                  className={cn(
                    'h-[14px] w-[14px] flex-none',
                    active ? 'text-[var(--color-chrome-accent-ink)]' : 'text-[var(--color-chrome-accent-ink)]/70',
                  )}
                  fill="currentColor"
                  strokeWidth={1.5}
                />
                {!collapsed && (
                  <span className="min-w-0 flex-1 truncate text-[12px] font-semibold">
                    {f.name}
                  </span>
                )}
              </Link>
              {!collapsed && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    removeFavorite.mutate(f.id);
                  }}
                  title={t('favorites.removeTitle')}
                  aria-label={t('favorites.removeTitle')}
                  className="absolute right-1.5 top-1/2 grid h-6 w-6 -translate-y-1/2 place-items-center rounded text-[var(--color-chrome-fg-muted)] opacity-0 transition-all hover:bg-[var(--color-chrome-bg)] hover:text-[var(--color-error)] group-hover/fav:opacity-100 focus-visible:opacity-100"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
